// SecureMaintPro — NL/EN translation dictionaries
// Modular, easily swappable. Access via useApp().t

export const translations = {
  nl: {
    langName: "NL",
    nav: {
      features: "Functies",
      disciplines: "Disciplines",
      ai: "AI",
      pricing: "Prijzen",
      trial: "Gratis proberen",
      downloads: "Downloads",
      activation: "Activatie",
      academy: "Academy",
      faq: "FAQ",
      contact: "Contact",
      cta: "Vraag Demo Aan",
    },
    hero: {
      badge: "Ontwikkeld met aandacht voor VEB · BORG · CCV-richtlijnen",
      title:
        "SecureMaintPro – Het Offline-First Logboek & Onderhoudsplatform voor Beveiligingstechnici.",
      subtitle:
        "Sluitende NEN-rapportages, volledige werkbon-traceerbaarheid en ingebouwde discipline-logboeken. 100% lokaal, offline-first en ontworpen zonder verplichte cloudverbinding.",
      ctaPackages: "Bekijk Pakketten",
      ctaDemo: "Vraag Demo Aan",
      trust: [
        "100% Offline-First",
        "AES-256 lokale back-ups",
        "Audit-traceerbaarheid",
      ],
      stats: [
        { value: "8", label: "Disciplines" },
        { value: "100%", label: "Offline" },
        { value: "NEN", label: "2535 / 2575" },
        { value: "AES-256", label: "Backups" },
      ],
    },
    value: {
      label: "Kernwaarden",
      title: "Vier principes die SecureMaintPro onderscheiden",
      subtitle:
        "Van inspectie tot opvolging — gebouwd rondom de realiteit van het werk in het veld.",
      cards: [
        {
          tag: "Structuur",
          title: "Minder zoeken, meer structuur",
          desc: "Een ijzersterke, consistente workflow van inspectie tot opvolging, met glasheldere rapportages die direct begrijpelijk zijn voor uw eindklant.",
        },
        {
          tag: "Autonoom",
          title: "100% Offline-First & Autonoom",
          desc: "Geen verplichte, trage cloud-verbindingen. Maximale privacy en operationele snelheid, overal functioneel — zelfs op locaties zonder enig bereik.",
        },
        {
          tag: "Integratie",
          title: "Naadloze Systeem-Integratie",
          desc: "Wij vervangen uw huidige ERP of klantsysteem niet. SecureMaintPro versterkt uw monteurs in het veld en levert data aan via universele PDF-, .MHR- of SharePoint-bestanden.",
        },
        {
          tag: "Scheiding",
          title: "Strikte Workflow-Scheiding",
          desc: "Waterdichte, rollen-gestuurde filters tussen de uitvoering door de monteur en de kwaliteitscontrole door de beoordelaar.",
        },
      ],
    },
    usp: {
      label: "De Positie",
      title: "Wij vervangen niets — wij versterken uw workflow in het veld",
      subtitle:
        "SecureMaintPro fungeert als een standalone, offline invoerschil. Data-overdracht en opvolging verlopen naadloos via universele exports.",
      workflow: ["Inspectie", "Rapport", "Werkbon", "Opvolging", "Historie"],
      exportsLabel: "Universele exports",
      exports: [
        "PDF-rapportages",
        "Werkbonnen",
        ".MHR-bestanden",
        "SharePoint",
        "ERP / Werkordersysteem",
      ],
    },
    audience: {
      label: "Voor wie?",
      title: "Voor wie is SecureMaintPro?",
      subtitle:
        "Eén vlijmscherpe tool — schaalbaar van de zelfstandige monteur tot het complete beveiligingsbedrijf.",
      cards: [
        {
          tag: "ZZP'er",
          title: "De Alles-in-één Tool",
          desc: "Geen behoefte aan ingewikkelde, dure cloud-software. SecureMaintPro geeft u een direct inzetbaar, 100% offline werkbonnen- en rapportagesysteem — alles om inspecties, logboeken en opvolgacties professioneel te beheren vanaf uw eigen laptop. Simpel, snel en zonder doorlopende hostingkosten.",
        },
        {
          tag: "Grotere bedrijven",
          title: "De Slimme Schil in het Veld",
          desc: "U hoeft uw bestaande software of kostbare ERP-systemen niet te vervangen. SecureMaintPro is de vlijmscherpe, 100% offline invulschil voor uw monteurs op locatie. Dankzij universele exports (.MHR, SharePoint, gecomprimeerde PDF's) schiet u inspectiedata moeiteloos door naar uw centrale ERP- of planningssysteem.",
        },
      ],
    },
    whatsnew: {
      tag: "NIEUW · v1.4.2",
      title: "Het Reviewer-dashboard meet nu de ROI van de AI-Assistent.",
      desc: "4 KPI-tegels, een 12-maands trendlijn en een 1-klik branded maand-rapport (PDF + CSV). Direct bruikbaar in management-overleg of bij license-renewals.",
      cta: "Lees meer over de Efficiëntie Cockpit",
    },
    disciplines: {
      label: "Disciplines",
      title: "8 disciplines, één offline platform",
      subtitle:
        "Van camerabewaking tot ICT-werkstations — alles in één consistente rapportage-workflow.",
      newBadge: "NIEUW",
      items: [
        { name: "CCTV", desc: "Camerabewaking (Analoog/IP toggle)" },
        { name: "Intrusion", desc: "Inbraakdetectie / BORG" },
        { name: "Access", desc: "Toegangscontrole" },
        { name: "Intercom", desc: "Video- en audio-intercom" },
        { name: "Fire", desc: "Brandmeldcentrale (NEN 2535)" },
        { name: "Evac", desc: "Ontruimingsinstallatie (NEN 2575)" },
        { name: "OP-taken", desc: "Operationele puntenlijst" },
        { name: "ICT / Windows", desc: "Werkstations, server, netwerk" },
      ],
    },
    ai: {
      label: "Offline AI-Assistent",
      tierBadge: "Alleen in Expert-licentie",
      title: "Een offline AI-assistent die je hardware-lijst leest.",
      subtitle:
        "Sleep een XLSX, CSV of DOCX in de assistent. Modelnummers worden gematcht tegen het schema, assets gaan naar de juiste discipline, en de classificatie wordt onthouden voor het volgende jaarlijkse rapport.",
      philosophy: {
        title: "Een hulpmiddel, geen werk-overname",
        intro:
          "Zie onze assistent niet als een zelfrijdende auto, maar als een digitaal kladblok dat op de achtergrond meeschrijft. De monteur blijft de inspectie doen — de assistent neemt alleen het saaie typewerk uit handen.",
        points: [
          {
            title: "De monteur blijft in de lead",
            desc: "De monteur doet de inspectie; de assistent vangt de bulk op — zoals 10 IP-adressen op een rij — zodat dat niet handmatig getypt hoeft te worden.",
          },
          {
            title: "De app dwingt u tot niets",
            desc: "Begrijpt de assistent een zin een keer niet? Geen probleem. Dan typt de monteur het gewoon ouderwets zelf in.",
          },
          {
            title: "Lokale Asset Memory die leert van goedgekeurde correcties",
            desc: "Corrigeert de monteur de assistent één keer bij een nieuw cameramodel? Dan onthoudt de lokale database dat voor de volgende keer. De software past zich aan aan de monteur — niet andersom.",
          },
        ],
      },
      stepsTitle: "Hoe het werkt",
      steps: [
        { title: "Drop een bestand", desc: "XLSX, CSV of DOCX. Parsing gebeurt via Electron-IPC, off the main thread." },
        { title: "AI tokeniseert", desc: "Modelnummers, merken en locaties worden herkend met regex + woordenboek-match." },
        { title: "Confidence-score", desc: "Bij twijfel toont de UI een 'AI Intervention'-paneel; jij keurt goed of corrigeert." },
        { title: "Knowledge Base groeit", desc: "Elke goedgekeurde match wordt onthouden — de volgende keer 100% confidence." },
      ],
      faqTitle: "Veelgestelde vragen",
      faq: [
        { q: "Gaat mijn data naar OpenAI/Google?", a: "Nee. Alle parsing gebeurt client-side via Electron-IPC. Geen telemetrie, geen API-calls." },
        { q: "Welke bestandsformaten?", a: "XLSX, CSV en DOCX." },
        { q: "Wat is Asset Memory?", a: "Per klant onthoudt het systeem IP/MAC/firmware/zone-info zodat een jaarlijks vervolgrapport meteen gevuld start." },
        { q: "Kan ik geleerde patronen delen?", a: "Ja, via JSON-export/import in Instellingen → AI Kennisdatabank." },
      ],
    },
    roi: {
      label: "Efficiëntie Cockpit",
      title: "Meet wat de AI-Assistent je team oplevert.",
      subtitle:
        "Per Reviewer een dashboard met 4 KPI's, een 12-maands trendlijn en een 1-klik maand-rapport voor management.",
      kpis: [
        { name: "Geleerde patronen", desc: "# rijen in het AI-woordenboek (model → discipline)" },
        { name: "AI-classificaties", desc: "Hoe vaak de AI succesvol een asset heeft geclassificeerd" },
        { name: "Onthouden assets", desc: "Unieke assets in Asset Memory (IP/MAC/firmware/zone)" },
        { name: "Tijdsbesparing", desc: "hits × 15s + assets × 30s (conservatieve, auditeerbare schatting)" },
      ],
      trendTitle: "12-maands trendlijn",
      trendDesc:
        "Bij elke cockpit-load wordt een snapshot bewaard. De Reviewer ziet de groeicurve van AI-adoptie — een sterk argument bij license-renewals.",
      reportTitle: "1-klik maand-rapport",
      reportDesc:
        "Branded export: PDF (cover in je merk-kleur + logo, 5 KPI-tegels, top-10 patronen, top-5 klanten) en CSV (RFC 4180, UTF-8 BOM, NL/EN headers). Bestandsnaam: {merk}-ai-roi-YYYY-MM.{pdf,csv}.",
    },
    whitelabel: {
      label: "Whitelabel / OEM",
      title: "Uw merk, in de software én in elk rapport.",
      subtitle:
        "Logo, accentkleur en tagline in één paneel — doorgevoerd in zowel het dashboard als de PDF-exports.",
      points: [
        "Eigen logo in dashboard en PDF-cover",
        "Accentkleur afgestemd op uw huisstijl",
        "Eigen tagline op rapportages",
        "Ideaal voor OEM- en installatiepartners",
      ],
    },
    matrix: {
      label: "Licentie-vergelijking",
      title: "Wat zit er in elke tier?",
      subtitle:
        "Dezelfde transparante tarieven en licentiestructuur voor elk pakket.",
      tierLabels: { feature: "Feature", standard: "Standard", professional: "Professional", expert: "Expert" },
      newTag: "NIEUW",
      rows: [
        { feature: "3 kerndisciplines (CCTV, Toegangscontrole, Inbraak)", s: true, p: true, e: true },
        { feature: "+ 2 disciplines (Intercom, OP-taken / NEN 2654)", s: false, p: true, e: true },
        { feature: "+ 3 disciplines (Brandmeld, Ontruiming/OAI, ICT/Windows)", s: false, p: false, e: true },
        { feature: "PDF-export Intern / Klant (split flows)", s: true, p: true, e: true },
        { feature: "BORG accumeting (multi-cell)", s: true, p: true, e: true },
        { feature: ".mhr AES-256 backup / restore", s: true, p: true, e: true },
        { feature: "Ed25519 licentie-signing", s: true, p: true, e: true },
        { feature: "Foto-embedding + CCTV Beeldenboek-generator", s: false, p: true, e: true },
        { feature: "Logboek (OP-taken)", s: false, p: true, e: true },
        { feature: "Logboeken per discipline (volledig)", s: false, p: false, e: true },
        { feature: "NEN-onderhoudsagenda / scheduler", s: false, p: false, e: true },
        { feature: "Offline AI-Assistent (XLSX/CSV/DOCX)", s: false, p: false, e: true },
        { feature: "AI Kennisdatabank beheer", s: false, p: false, e: true },
        { feature: "Efficiëntie Cockpit (ROI)", s: false, p: false, e: true },
        { feature: "Maandelijks ROI-rapport (PDF + CSV)", s: false, p: false, e: true },
        { feature: "Whitelabel / OEM Branding", s: false, p: false, e: true },
        { feature: "Optionele cloud-portal sync (klantportaal · +€24,99/mnd)", s: false, p: true, e: true },
      ],
      whoTitle: "Wie kiest welke tier?",
      who: [
        { tier: "Standard", desc: "Kleine installateurs (1-3 monteurs) die alleen NL/EN PDF-rapporten en lokale opslag nodig hebben." },
        { tier: "Professional", desc: "Installateurs die ook Intercom & OP-taken (NEN 2654) doen en foto's + logboek willen vastleggen. De sweet spot." },
        { tier: "Expert", desc: "Multi-monteur teams en OEM/whitelabel-partners die ROI-rapportage richting management/klanten leveren." },
      ],
      custom:
        "Maatwerk: elke combinatie van disciplines + features is mogelijk via een custom .lic bundel — bijv. alleen Fire+Evac voor BORG-erkende brandinstallateurs.",
    },
    changelog: {
      label: "Changelog",
      title: "Releasenotes",
      version: "v1.4.2 — 13 juni 2026",
      groups: [
        { title: "Nieuw", items: ["Reviewer Efficiëntie Cockpit met 4 KPI-tegels + 12-maands sparkline", "1-klik maandelijks ROI-rapport (PDF + CSV)", "AI Kennisdatabank beheer-UI (zoek/filter/delete/JSON-export-import)", "ICT/Windows als 8e discipline"] },
        { title: "Verbeterd", items: ["Schema-aware AI met Confidence Guardrails (v1.4)", "Internal vs Customer split PDF-export", "Whitelabel branding panel (logo + accent + tagline)"] },
        { title: "Onder de motorkap", items: ["IndexedDB schema v7 (ai_roi_snapshots store)", "229 pure-Node tests, ESLint clean", "Asynchrone PDF/DOCX parsing via Electron IPC"] },
      ],
    },
    pricing: {
      label: "Pakketten",
      title: "Transparante tarieven — geen trucjes",
      subtitle:
        "Geen misleidende kortingsbadges of tijdelijke marketingacties. Gewoon heldere tarieven per maand of per jaar.",
      popular: "Meest gekozen",
      cta: "Bestel nu",
      cloudAddon: {
        line: "Optionele cloud-portal sync — klanten online toegang geven (+€24,99/maand)",
        standardNote: "Klantportaal: upgrade naar Professional.",
      },
      billing: {
        monthly: "Maandelijks",
        annual: "Jaarlijks",
      },
      annualEquiv: "omgerekend",
      microSub: "Inclusief alle software-updates & support",
      period: {
        monthly: "/maand",
        annual: "/jaar",
      },
      tiers: [
        {
          id: "standard",
          name: "Standard",
          desc: "Voor de kleine installateur — 3 kerndisciplines.",
          prices: { monthly: "€24,95", annual: "€299,40" },
          features: [
            "3 disciplines: CCTV, Toegangscontrole & Inbraakalarm (IMI/BORG)",
            "PDF-export Intern / Klant (split flows)",
            "BORG accumeting (multi-cell)",
            ".mhr AES-256 backup / restore",
            "Ed25519 licentie-signing",
          ],
          note: "Zonder foto-embedding, logboek, AI-Assistent & scheduler.",
        },
        {
          id: "professional",
          name: "Gevorderde",
          desc: "Standard + 2 disciplines, foto's en logboek.",
          prices: { monthly: "€39,95", annual: "€479,40" },
          features: [
            "Alles uit Standard",
            "+ 2 disciplines: Intercom & OP-taken (NEN 2654)",
            "Foto-embedding + CCTV Beeldenboek-generator",
            "Logboek-functie (gericht op OP-taken)",
          ],
          note: "AI-Assistent & global scheduler zitten in Expert.",
        },
        {
          id: "expert",
          name: "Expert",
          desc: "Alle 8 disciplines, AI-Assistent en volledige logboeken.",
          prices: { monthly: "€59,95", annual: "€719,40" },
          features: [
            "Alle 8 disciplines (incl. Brandmeld, Ontruiming/OAI & ICT/Windows)",
            "Offline AI-Assistent (XLSX/CSV/DOCX classificatie)",
            "Logboeken per discipline (volledig)",
            "Global scheduler / NEN-onderhoudsagenda",
            "Efficiëntie Cockpit: ROI-dashboard + maandrapport",
            "Whitelabel / OEM branding",
            "Prioritaire ondersteuning & directe updates",
          ],
        },
      ],
    },
    trial: {
      label: "Gratis Proeflicentie",
      badge: "7 dagen · Expert · 100% functioneel",
      title: "Probeer SecureMaintPro 7 dagen gratis in de praktijk",
      subtitle:
        "Vraag een gratis, volledig functionele 7-daagse Expert-testlicentie aan. Ervaar alle 8 disciplines, inclusief de automatische NEN-keuringsplanner en foto-annotaties, direct op uw eigen hardware.",
      stepsTitle: "Hoe werkt het aanvragen?",
      downloadBtn: "Download SecureMaintPro (.EXE)",
      steps: [
        {
          title: "Download de software",
          desc: "Download de standalone Windows-applicatie via de knop hieronder. Er is geen installatie vereist.",
        },
        {
          title: "Kopieer uw Hardware-ID",
          desc: "Start de software op uw laptop. Het activatiescherm opent automatisch en toont uw unieke, lokale Hardware-ID (bijv. SMP-ABCD-1234).",
        },
        {
          title: "Vraag uw testlicentie aan",
          desc: "Vul het formulier in en vermeld uw Hardware-ID. Wij sturen uw persoonlijke testlicentie (.lic) binnen enkele uren rechtstreeks naar uw mailbox.",
        },
      ],
      form: {
        title: "Aanvraagformulier testlicentie",
        company: "Bedrijfsnaam",
        companyPh: "Uw bedrijfsnaam",
        name: "Naam contactpersoon",
        namePh: "Voor- en achternaam",
        email: "E-mailadres",
        emailPh: "jij@bedrijf.nl",
        phone: "Telefoonnummer",
        phonePh: "+31 6 12 34 56 78",
        hardwareId: "Uw unieke Hardware-ID",
        hardwareIdPh: "bijv. SMP-ABCD-1234",
        submit: "Gratis testlicentie aanvragen",
        noteBefore:
          "Uw aanvraag wordt handmatig of via ons systeem verwerkt. Voor directe vragen of support kunt u mailen naar",
      },
      success: {
        title: "Aanvraag verzonden!",
        body: "Uw mailprogramma is geopend met uw testlicentie-aanvraag. Verstuur de e-mail; u ontvangt uw .lic-bestand binnen enkele uren.",
        close: "Sluiten",
      },
      mail: {
        subject:
          "Aanvraag Gratis Testlicentie / Free Trial Request - SecureMaintPro",
        labels: {
          company: "Bedrijf / Company",
          name: "Contactpersoon / Contact",
          email: "E-mail / Email",
          phone: "Telefoon / Phone",
          hardwareId: "Hardware-ID",
        },
      },
      errors: {
        company: "Bedrijfsnaam is verplicht",
        name: "Naam is verplicht",
        email: "Voer een geldig e-mailadres in",
        phone: "Telefoonnummer is verplicht",
        hardwareId: "Hardware-ID is verplicht",
      },
    },
    downloads: {
      label: "Resources & Downloads",
      title: "Direct aan de slag — geen account nodig",
      subtitle:
        "Publieke toegang tot de productdocumentatie en de beveiligde Windows-installer.",
      manual: {
        title: "Installatiehandleiding (PDF)",
        desc: "Complete stap-voor-stap gids voor installatie, configuratie en eerste gebruik.",
        meta: "PDF · Tweetalig (NL/EN) · 290 KB",
        btn: "Download Handleiding",
      },
      installer: {
        title: "Windows Installer (.exe)",
        desc: "Beveiligde, gesigneerde installer voor Windows 10/11. Inclusief auto-update.",
        meta: "SecureMaintPro-Setup-1.0.0.exe · 79 MB",
        platform: "v1.0.0 · Windows 10/11 · 64-bit",
        checksumLabel: "SHA-256",
        checksum: "344c261de9b4f134dfc9307c0ac4ca6c6fb140b5d7393abea7dcaeca22e901f9",
        btn: "Download Installer",
      },
      note: "Officiële release-bestanden (v1.0.0). Download direct — geen account nodig.",
      copied: "SHA-256 gekopieerd",
      soon: "Binnenkort beschikbaar",
      started: "Download gestart",
    },
    activation: {
      label: "Licentie-Activatie",
      title: "Geactiveerd in drie eenvoudige stappen",
      subtitle:
        "Van installer tot werkende licentie in enkele minuten — volledig offline.",
      hwMail: {
        btn: "Hardware-ID mailen",
        subject: "Hardware-ID indienen - SecureMaintPro",
      },
      steps: [
        {
          title: "Download de Installer",
          desc: "Haal de gesigneerde Windows-installer op uit de Downloads-hub en voer de installatie uit.",
        },
        {
          title: "Auto-Fetch Hardware-ID",
          desc: "Bij eerste start leest SecureMaintPro automatisch je unieke Hardware-ID uit voor de licentiebinding.",
        },
        {
          title: "Drag-and-drop securemaint.lic",
          desc: "Sleep je ontvangen securemaint.lic bestand in het venster — direct geactiveerd, geen internet vereist.",
        },
      ],
    },
    faq: {
      label: "Veelgestelde vragen",
      title: "Alles wat je wilt weten",
      subtitle:
        "Heldere antwoorden over offline licenties, dataopslag en beveiliging.",
      items: [
        {
          q: "Hoe werkt een offline licentie?",
          a: "De app genereert een unieke hardwarecode op uw laptop. Wij ondertekenen dit cryptografisch (Ed25519) in een .lic-bestand dat de app 100% lokaal en offline valideert.",
        },
        {
          q: "Waar blijft mijn data?",
          a: "Standaard volledig lokaal op uw C-schijf — SecureMaintPro werkt 100% offline en draait zonder internet. De enige uitzondering is de optionele cloud-portal sync (Professional & Expert): als u die activeert, uploaden uw monteurs uitsluitend de door hen geselecteerde onderhoudsrapporten en werkbonnen naar uw eigen, geïsoleerde klantportaal. Zonder die optie verlaat er nooit data uw machine.",
        },
        {
          q: "Zijn software-updates inbegrepen?",
          a: "Ja. Bij zowel maandelijkse als jaarlijkse licenties heeft u gedurende de actieve looptijd onbeperkt recht op alle software-updates, bugfixes en support.",
        },
        {
          q: "Kan ik een actieve licentie tussentijds wijzigen of annuleren?",
          a: "Nee, SecureMaintPro hanteert een strikt \"gekocht is gekocht\" beleid. Licenties worden vooraf voor de gekozen periode (maand of jaar) aangeschaft per uniek Hardware ID. Tussentijdse annulering, restitutie of pro-rata verrekening is niet mogelijk. Bij een upgrade binnen een lopende periode start een nieuwe, volledige licentietermijn voor het gekozen pakket.",
        },
        {
          q: "Wat is het klantportaal en heb ik het nodig?",
          a: "Het klantportaal is een online omgeving waarin uw klanten 24/7 toegang hebben tot hun onderhoudsrapporten en werkbonnen. Het is een optionele add-on op Professional en Expert software. Zonder portaal blijft alle data lokaal — mét portaal kunnen uw klanten zelf inloggen en rapporten tekenen.",
        },
        {
          q: "Wat kost het klantportaal?",
          a: "€24,99 per maand (jaarlijks gefactureerd, ≈ €299,88/jaar). Onbeperkt aantal gebruikers per klant-organisatie inbegrepen.",
        },
        {
          q: "Is gebruik van het klantportaal verplicht?",
          a: "Nee. De cloud-sync in Professional en Expert software is volledig optioneel. Uw monteurs kunnen kiezen om wel of niet te uploaden — als de licentie niet actief is werkt de software gewoon offline verder.",
        },
        {
          q: "Mijn klant heeft Standard — kan die toch het portaal gebruiken?",
          a: "Niet rechtstreeks. Het portaal is alleen beschikbaar bij Professional en Expert software-licenties. Een upgrade van Standard naar Professional kan hiervoor geregeld worden.",
        },
        {
          q: "Hoe veilig zijn de gegevens in het portaal?",
          a: "Elke klant-organisatie is een geïsoleerde tenant — geen andere klant kan de data zien. Alle login-pogingen, downloads en wijzigingen worden gelogd in een audit-trail. PDF's worden versleuteld opgeslagen.",
        },
      ],
    },
    contact: {
      label: "Bestellen & Contact",
      title: "Bestel je licentie of stel een vraag",
      subtitle:
        "Vul het formulier in. Bij verzenden opent automatisch je mailprogramma met een nette, voor-ingevulde bestelling.",
      fields: {
        name: "Naam klant",
        namePh: "Voor- en achternaam",
        email: "Contact e-mail",
        emailPh: "jij@bedrijf.nl",
        hardwareId: "Hardware-ID",
        hardwareIdPh: "16 tekens, bijv. 7F3A9B2C4D5E6F70",
        package: "Pakket",
        packagePh: "Selecteer een pakket",
        duration: "Looptijd",
        durationPh: "Selecteer looptijd",
        message: "Bericht (optioneel)",
        messagePh: "Eventuele toelichting of configuratievraag...",
      },
      packages: {
        standard: "Standard",
        professional: "Professional",
        expert: "Expert",
        inquiry: "Algemene vraag",
      },
      durations: {
        monthly: "Maandelijks",
        annual: "Jaarlijks",
      },
      submit: "Verstuur & Open Mail",
      success: {
        title: "Bestelling klaar!",
        body: "Je mailprogramma is geopend met een voor-ingevulde bestelling. Verstuur de e-mail om je aanvraag definitief te maken.",
        close: "Sluiten",
      },
      mail: {
        subject: "Nieuwe Bestelling / New Order - SecureMaintPro",
        labels: {
          client: "Klant / Client",
          tier: "Pakket / Tier",
          duration: "Looptijd / Duration",
          hardwareId: "Hardware-ID",
          message: "Bericht / Message",
        },
      },
      errors: {
        name: "Naam is verplicht",
        email: "Voer een geldig e-mailadres in",
        package: "Selecteer een pakket",
        duration: "Selecteer een looptijd",
        hardwareId: "Hardware-ID moet exact 16 alfanumerieke tekens zijn",
      },
    },
    footer: {
      tagline:
        "Het offline-first logboek- en onderhoudsplatform voor beveiligingstechnici.",
      product: "Product",
      resources: "Resources",
      legal: "Juridisch",
      links: {
        features: "Functies",
        pricing: "Prijzen",
        downloads: "Downloads",
        activation: "Activatie",
        manual: "Handleiding",
        contact: "Contact",
        privacy: "Privacybeleid",
        terms: "Voorwaarden",
        compliance: "Compliance",
      },
      rights: "Alle rechten voorbehouden.",
      version: "",
    },
    compliance: {
      label: "Legal, Privacy & Compliance",
      title: "Afgestemd op NEN 2654 & gebouwd op Privacy by Design",
      cards: [
        {
          title: "Richtlijnen NEN 2654-1 & NEN 2654-2",
          desc: "De rapportage- en checklistmodules voor de OP-taken zijn functioneel afgestemd op de framework-structuur van NEN 2654-1 en NEN 2654-2. De software is niet getoetst of gecertificeerd door VEB, CCV of NEN; wij streven er wél naar te werken volgens hun richtlijnen. De software dient als digitaal hulpmiddel om verplichte registraties overzichtelijk vast te leggen in een lokaal logboek. De gebruiker blijft zelf verantwoordelijk voor de correcte uitvoering en toetsing in de praktijk.",
        },
        {
          title: "Privacy & AVG",
          desc: "SecureMaintPro is gebouwd volgens Privacy by Design. Alle ingevoerde operationele data wordt strikt lokaal opgeslagen op de hardware van de licentienemer. Wij hebben geen toegang tot uw databases of klantgegevens. De licentienemer fungeert juridisch als de Verwerkingsverantwoordelijke conform de AVG.",
        },
        {
          title: "AI draait 100% client-side",
          desc: "XLSX, CSV en DOCX worden lokaal geparsed via Electron-IPC. Geen bestanden, geen tokens, geen telemetrie gaat ooit naar een externe server.",
        },
      ],
      contactLabel: "Vragen, licentie-activaties of support?",
      contactBtn: "Mail ons",
    },
    terms: {
      title: "Algemene Verkoopvoorwaarden",
      subtitle: "Lees onze licentie- en verkoopvoorwaarden zorgvuldig door.",
      close: "Sluiten",
      link: "Algemene Verkoopvoorwaarden",
      articles: [
        {
          title: "Artikel 1 — Licentie-structuur & Hardware-binding",
          items: [
            "1.1 Licenties voor SecureMaintPro worden uitsluitend verstrekt op basis van een unieke Hardware-ID (apparaat-vingerafdruk).",
            "1.2 Een gegenereerd licentiebestand (.lic) is strikt gebonden aan de specifieke laptop/machine waarvoor deze is aangevraagd. Het overdragen van een actieve licentie naar een andere machine of hardware-ID is tussentijds niet toegestaan.",
            "1.3 De Hardware-ID dient rechtstreeks vanuit de software te worden gekopieerd en kan daardoor nagenoeg niet foutief worden doorgegeven. De Licentienemer is volledig verantwoordelijk voor het naar waarheid en correct aanleveren van de Hardware-ID. Omdat de software volledig offline werkt, kan de Licentiegever de juistheid hiervan niet verifiëren. Bij het opgeven van een onjuiste Hardware-ID wordt de licentie alsnog op dat (foutieve) ID gegenereerd; er volgt geen restitutie of kosteloze heruitgifte en dient een nieuwe licentie te worden aangeschaft.",
            "1.4 Bij verlies, diefstal, defect of vervanging van de laptop/machine waaraan de licentie is gebonden, kan de licentie niet worden overgezet of hersteld. Omdat activatie volledig offline plaatsvindt, kan de Licentiegever dit niet controleren. In dat geval geldt 'gekocht is gekocht' en dient een nieuwe licentie te worden aangeschaft.",
          ],
        },
        {
          title: 'Artikel 2 — "Gekocht is gekocht" en upgrades',
          items: [
            "2.1 SecureMaintPro hanteert een strikt non-refundable beleid. Gezien de aard van digitale, offline activatie is restitutie, annulering of pro-rata (naar rato) verrekening van reeds betaalde termijnen uitgesloten.",
            "2.2 Indien een Licentienemer tussentijds wenst te upgraden (bijvoorbeeld van het Standard- naar het Expert-pakket), vervalt de lopende licentieperiode. Er start op dat moment een nieuwe, volledige licentietermijn (maand of jaar) tegen het tarief van het nieuw gekozen pakket. Resterende maanden van de oude licentie worden niet financieel gecompenseerd of verrekend.",
          ],
        },
        {
          title: "Artikel 3 — Software-updates & support",
          items: [
            "3.1 Maandelijkse en jaarlijkse abonnementslicenties geven recht op alle functionele software-updates, bugfixes en support gedurende de actieve, betaalde contractperiode.",
            "3.2 SecureMaintPro wordt uitsluitend aangeboden als maandelijkse of jaarlijkse abonnementslicentie. Toekomstige grote architectonische upgrades (zoals een overgang naar v2.0.0) worden binnen een actief abonnement beschikbaar gesteld conform het dan geldende updatebeleid.",
          ],
        },
        {
          title: "Artikel 4 — Anti-fraude en accountblokkade",
          items: [
            "4.1 De software beschikt over ingebouwde mechanismen voor offline fraudedetectie, waaronder anti-klokfraude (systeemtijd-manipulatie). Indien het systeem manipulatie van de interne klok of de licentiestructuur detecteert, wordt de applicatie onmiddellijk lokaal vergrendeld.",
            "4.2 Licentienemer is zelf verantwoordelijk voor het correct instellen en handhaven van de actuele systeem-/netwerktijd op de machine. Een lokale vergrendeling als gevolg van gedetecteerde klokfraude (systeemtijd-manipulatie) wordt door de Licentiegever niet beoordeeld, ongedaan gemaakt of hersteld. In dat geval vervalt het gebruiksrecht op de betreffende machine en dient een nieuwe licentie te worden aangeschaft.",
          ],
        },
      ],
    },
    shop: {
      nav: "Bestellen",
      vat: "excl. btw",
      title: "Bestel je licentie",
      subtitle: "Kies je pakket en reken direct af via iDEAL. Je factuur en activatiecode ontvang je per e-mail.",
      billingNote: "Alle prijzen zijn excl. 21% btw.",
      addonTitle: "Klantportaal toevoegen",
      addonDesc: "Onbeperkt gebruikers · online rapport-inzage & digitale ondertekening.",
      addonStandard: "Upgrade naar Professional of Expert om het klantportaal toe te voegen.",
      choose: "Kies",
      soon: "Binnenkort beschikbaar",
      checkoutTitle: "Bestelling afronden",
      summary: "Je bestelling",
      emailLabel: "E-mailadres",
      emailPh: "jij@bedrijf.nl",
      companyLabel: "Bedrijfsnaam",
      companyPh: "Uw bedrijfsnaam",
      kvkLabel: "KVK-nummer (optioneel)",
      kvkPh: "12345678",
      termsPrefix: "Ik ga akkoord met de",
      termsLink: "Algemene Verkoopvoorwaarden",
      pay: "Betaal via iDEAL",
      processing: "Bezig…",
      back: "Terug naar shop",
      subtotal: "Subtotaal (excl. btw)",
      vatLine: "Btw (21%)",
      total: "Totaal (incl. btw)",
      portalAddon: "Klantportaal add-on",
      portalYearly: "Jaarlijkse licentie",
      addonPriceLabel: "+€299,88/jaar",
      perYear: "/jaar",
      oneTime: "eenmalig",
      termsRequired: "Ga akkoord met de voorwaarden om verder te gaan.",
      emailRequired: "Vul een geldig e-mailadres in.",
      unavailable: "Online betalen wordt binnenkort geactiveerd. Neem voorlopig contact op via e-mail.",
      error: "Er ging iets mis bij het starten van de betaling. Probeer het later opnieuw.",
      orderEmail: "Bestelling verzenden via e-mail",
      mailSubject: "Bestelling SecureMaintPro",
      mailIntro: "Ik wil graag de volgende SecureMaintPro-licentie bestellen:",
      mailSent: "Je bestelling is klaargezet in je e-mailprogramma — verstuur de e-mail om je bestelling af te ronden.",
      thankTitle: "Bedankt voor je bestelling!",
      thankBody: "Je ontvangt binnen enkele minuten een e-mail met je factuur en activatiecode. Gebruik de activatiecode in de SecureMaintPro-app om je licentie te koppelen.",
      thankActivate: "Activatie-instructies",
      thankHome: "Terug naar home",
      activateTitle: "Je licentie activeren",
      activateSubtitle: "Volg deze stappen nadat je je activatiecode per e-mail hebt ontvangen.",
    },
    portal: {
      navLabel: "Klantportaal",
      eyebrow: "Klantportaal add-on",
      title: "Geef uw klanten 24/7 inzage in hun rapporten",
      lead: "Met de optionele cloud-sync in onze Professional en Expert software uploaden uw monteurs onderhoudsrapporten en werkbonnen automatisch naar het klantportaal. Uw klant logt in op portaal.securemaintpro.nl en heeft direct toegang.",
      features: [
        { title: "Rapporten tekenen", desc: "Klant ondertekent onderhoudsrapporten digitaal — direct in de browser." },
        { title: "Werkbonnen inzien", desc: "Volledig overzicht van alle werkbonnen en gerapporteerde defecten." },
        { title: "Multi-user · onbeperkt", desc: "Onbeperkt gebruikers per organisatie met rollen (admin · approver · viewer)." },
        { title: "Multi-tenant geïsoleerd", desc: "Strikte data-scheiding per organisatie + volledige audit-log." },
      ],
      card: {
        eyebrow: "Jaarlicentie",
        title: "SecureMaintPro · Klantportaal",
        price: "€24,99",
        period: "/maand",
        year: "≈ €299,88 per jaar · vaste prijs · jaarlijks factureren",
        includes: [
          "1 jaar toegang tot klantportaal",
          "Onbeperkt aantal gebruikers",
          "Alle functies: tekenen, werkbon-inzage, audit-log",
          "Automatische sync vanuit Pro/Expert desktop-software",
          "Multi-tenant geïsoleerd · end-to-end gelogd",
        ],
        eligibility: "Beschikbaar voor klanten met Professional of Expert desktop-software. Standard-klanten kunnen upgraden om gebruik te maken van het portaal.",
        ctaRequest: "Klantportaal aanvragen",
        ctaLogin: "Al klant? Inloggen",
      },
    },
    academy: {
      label: "Academy",
      title: "SecureMaintPro Academy",
      subtitle: "Bekijk stap-voor-stap video-instructies en zie de software in de praktijk — van installatie tot rapportage.",
      videoTitle: "Software-instructie — stap voor stap uitleg",
      videoDesc: "Checklists, onderhoud, rapportages en instellingen: alles helder uitgelegd in korte video's op ons YouTube-kanaal.",
      cta: "Bekijk video's op YouTube",
      channelNote: "Nieuwe video's verschijnen regelmatig op ons kanaal.",
      chips: ["Checklists", "Onderhoud", "Rapportages", "Instellingen"],
      linkedinCta: "Volg ons op LinkedIn",
      linkedinNote: "Blijf op de hoogte van updates, nieuwe releases en tips.",
    },
  },

  en: {
    langName: "EN",
    nav: {
      features: "Features",
      disciplines: "Disciplines",
      ai: "AI",
      pricing: "Pricing",
      trial: "Free trial",
      downloads: "Downloads",
      activation: "Activation",
      academy: "Academy",
      faq: "FAQ",
      contact: "Contact",
      cta: "Request Demo",
    },
    hero: {
      badge: "Aligned with VEB · BORG · CCV guidelines",
      title:
        "SecureMaintPro – The Offline-First Logbook & Maintenance Platform for Security Engineers.",
      subtitle:
        "Compliant NEN reporting, full work order traceability, and built-in discipline logbooks. 100% local, offline-first and designed without a mandatory cloud connection.",
      ctaPackages: "View Packages",
      ctaDemo: "Request Demo",
      trust: [
        "100% Offline-First",
        "AES-256 local backups",
        "Audit traceability",
      ],
      stats: [
        { value: "8", label: "Disciplines" },
        { value: "100%", label: "Offline" },
        { value: "NEN", label: "2535 / 2575" },
        { value: "AES-256", label: "Backups" },
      ],
    },
    value: {
      label: "Core values",
      title: "Four principles that set SecureMaintPro apart",
      subtitle:
        "From inspection to follow-up — built around the reality of field work.",
      cards: [
        {
          tag: "Structure",
          title: "Less searching, more structure",
          desc: "A rock-solid, consistent workflow from inspection to follow-up, with crystal-clear reports your end client understands immediately.",
        },
        {
          tag: "Autonomous",
          title: "100% Offline-First & Autonomous",
          desc: "No mandatory, slow cloud connections. Maximum privacy and operational speed, functional everywhere — even at sites with no signal at all.",
        },
        {
          tag: "Integration",
          title: "Seamless System Integration",
          desc: "We don't replace your current ERP or client system. SecureMaintPro empowers your engineers in the field and delivers data via universal PDF, .MHR or SharePoint files.",
        },
        {
          tag: "Separation",
          title: "Strict Workflow Separation",
          desc: "Watertight, role-driven filters between execution by the engineer and quality control by the reviewer.",
        },
      ],
    },
    usp: {
      label: "The Position",
      title: "We replace nothing — we strengthen your workflow in the field",
      subtitle:
        "SecureMaintPro acts as a standalone, offline input layer. Data transfer and follow-up happen seamlessly via universal exports.",
      workflow: ["Inspection", "Report", "Work order", "Follow-up", "History"],
      exportsLabel: "Universal exports",
      exports: [
        "PDF reports",
        "Work orders",
        ".MHR files",
        "SharePoint",
        "ERP / Work order system",
      ],
    },
    audience: {
      label: "Who is it for?",
      title: "Who is SecureMaintPro for?",
      subtitle:
        "One razor-sharp tool — scalable from the independent engineer to the complete security company.",
      cards: [
        {
          tag: "Freelancer",
          title: "The All-in-One Tool",
          desc: "No need for complex, expensive cloud software. SecureMaintPro gives you a ready-to-use, 100% offline work order and reporting system — everything to professionally manage inspections, logbooks and follow-up actions from your own laptop. Simple, fast and without recurring hosting costs.",
        },
        {
          tag: "Larger companies",
          title: "The Smart Shell in the Field",
          desc: "You don't have to replace your existing software or costly ERP systems. SecureMaintPro is the razor-sharp, 100% offline input shell for your engineers on-site. Thanks to universal exports (.MHR, SharePoint, compressed PDFs) you effortlessly push inspection data into your central ERP or planning system.",
        },
      ],
    },
    whatsnew: {
      tag: "NEW · v1.4.2",
      title: "The Reviewer dashboard now measures the ROI of the AI Assistant.",
      desc: "4 KPI tiles, a 12-month trend line and a 1-click branded monthly report (PDF + CSV). Ready for management meetings or license renewals.",
      cta: "Learn more about the Efficiency Cockpit",
    },
    disciplines: {
      label: "Disciplines",
      title: "8 disciplines, one offline platform",
      subtitle:
        "From camera surveillance to ICT workstations — all in one consistent reporting workflow.",
      newBadge: "NEW",
      items: [
        { name: "CCTV", desc: "Camera surveillance (Analog/IP toggle)" },
        { name: "Intrusion", desc: "Intrusion detection / BORG" },
        { name: "Access", desc: "Access control" },
        { name: "Intercom", desc: "Video and audio intercom" },
        { name: "Fire", desc: "Fire alarm panel (NEN 2535)" },
        { name: "Evac", desc: "Evacuation system (NEN 2575)" },
        { name: "OP tasks", desc: "Operational checklist" },
        { name: "ICT / Windows", desc: "Workstations, server, network" },
      ],
    },
    ai: {
      label: "Offline AI Assistant",
      tierBadge: "Expert license only",
      title: "An offline AI assistant that reads your hardware list.",
      subtitle:
        "Drop an XLSX, CSV or DOCX into the assistant. Model numbers are matched against the schema, assets go to the right discipline, and the classification is remembered for next year's report.",
      philosophy: {
        title: "A helper, not a replacement",
        intro:
          "Don't think of our assistant as a self-driving car, but as a digital notepad that quietly takes notes in the background. The engineer still does the inspection — the assistant just takes the tedious typing off their hands.",
        points: [
          {
            title: "The engineer stays in charge",
            desc: "The engineer does the inspection; the assistant catches the bulk — like 10 IP addresses in a row — so it doesn't have to be typed manually.",
          },
          {
            title: "It never forces your hand",
            desc: "Doesn't the assistant understand a line once? No problem. The engineer simply types it in the old-fashioned way.",
          },
          {
            title: "Local Asset Memory that learns from approved corrections",
            desc: "Does the engineer correct the assistant once for a new camera model? The local database remembers it for next time. The software adapts to the engineer — not the other way around.",
          },
        ],
      },
      stepsTitle: "How it works",
      steps: [
        { title: "Drop a file", desc: "XLSX, CSV or DOCX. Parsing happens via Electron-IPC, off the main thread." },
        { title: "AI tokenizes", desc: "Model numbers, brands and locations are recognized with regex + dictionary match." },
        { title: "Confidence score", desc: "When in doubt the UI shows an 'AI Intervention' panel; you approve or correct." },
        { title: "Knowledge Base grows", desc: "Every approved match is remembered — 100% confidence next time." },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "Does my data go to OpenAI/Google?", a: "No. All parsing happens client-side via Electron-IPC. No telemetry, no API calls." },
        { q: "Which file formats?", a: "XLSX, CSV and DOCX." },
        { q: "What is Asset Memory?", a: "Per client the system remembers IP/MAC/firmware/zone info so an annual follow-up report starts pre-filled." },
        { q: "Can I share learned patterns?", a: "Yes, via JSON export/import in Settings → AI Knowledge Base." },
      ],
    },
    roi: {
      label: "Efficiency Cockpit",
      title: "Measure what the AI Assistant delivers for your team.",
      subtitle:
        "A dashboard per Reviewer with 4 KPIs, a 12-month trend line and a 1-click monthly report for management.",
      kpis: [
        { name: "Learned patterns", desc: "# rows in the AI dictionary (model → discipline)" },
        { name: "AI classifications", desc: "How often the AI successfully classified an asset" },
        { name: "Remembered assets", desc: "Unique assets in Asset Memory (IP/MAC/firmware/zone)" },
        { name: "Time saved", desc: "hits × 15s + assets × 30s (conservative, auditable estimate)" },
      ],
      trendTitle: "12-month trend line",
      trendDesc:
        "On every cockpit load a snapshot is stored. The Reviewer sees the AI-adoption growth curve — a strong argument at license renewals.",
      reportTitle: "1-click monthly report",
      reportDesc:
        "Branded export: PDF (cover in your brand color + logo, 5 KPI tiles, top-10 patterns, top-5 clients) and CSV (RFC 4180, UTF-8 BOM, NL/EN headers). Filename: {brand}-ai-roi-YYYY-MM.{pdf,csv}.",
    },
    whitelabel: {
      label: "Whitelabel / OEM",
      title: "Your brand, in the software and in every report.",
      subtitle:
        "Logo, accent color and tagline in one panel — applied to both the dashboard and the PDF exports.",
      points: [
        "Your own logo in dashboard and PDF cover",
        "Accent color matched to your house style",
        "Your own tagline on reports",
        "Ideal for OEM and installation partners",
      ],
    },
    matrix: {
      label: "License comparison",
      title: "What's in each tier?",
      subtitle:
        "The same transparent rates and license structure for every plan.",
      tierLabels: { feature: "Feature", standard: "Standard", professional: "Professional", expert: "Expert" },
      newTag: "NEW",
      rows: [
        { feature: "3 core disciplines (CCTV, Access control, Intrusion)", s: true, p: true, e: true },
        { feature: "+ 2 disciplines (Intercom, OP tasks / NEN 2654)", s: false, p: true, e: true },
        { feature: "+ 3 disciplines (Fire, Evacuation/OAI, ICT/Windows)", s: false, p: false, e: true },
        { feature: "PDF export Internal / Customer (split flows)", s: true, p: true, e: true },
        { feature: "BORG battery measurement (multi-cell)", s: true, p: true, e: true },
        { feature: ".mhr AES-256 backup / restore", s: true, p: true, e: true },
        { feature: "Ed25519 licence signing", s: true, p: true, e: true },
        { feature: "Photo embedding + CCTV Image Book generator", s: false, p: true, e: true },
        { feature: "Logbook (OP tasks)", s: false, p: true, e: true },
        { feature: "Logbooks per discipline (full)", s: false, p: false, e: true },
        { feature: "NEN maintenance agenda / scheduler", s: false, p: false, e: true },
        { feature: "Offline AI Assistant (XLSX/CSV/DOCX)", s: false, p: false, e: true },
        { feature: "AI Knowledge Base management", s: false, p: false, e: true },
        { feature: "Efficiency Cockpit (ROI)", s: false, p: false, e: true },
        { feature: "Monthly ROI report (PDF + CSV)", s: false, p: false, e: true },
        { feature: "Whitelabel / OEM Branding", s: false, p: false, e: true },
        { feature: "Optional cloud-portal sync (customer portal · +€24.99/mo)", s: false, p: true, e: true },
      ],
      whoTitle: "Who picks which tier?",
      who: [
        { tier: "Standard", desc: "Small installers (1-3 engineers) who only need NL/EN PDF reports and local storage." },
        { tier: "Professional", desc: "Installers who also handle Intercom & OP tasks (NEN 2654) and want photos + logbook. The sweet spot." },
        { tier: "Expert", desc: "Multi-engineer teams and OEM/whitelabel partners delivering ROI reporting to management/clients." },
      ],
      custom:
        "Custom: any combination of disciplines + features is possible via a custom .lic bundle — e.g. only Fire+Evac for BORG-certified fire installers.",
    },
    changelog: {
      label: "Changelog",
      title: "Release notes",
      version: "v1.4.2 — 13 June 2026",
      groups: [
        { title: "New", items: ["Reviewer Efficiency Cockpit with 4 KPI tiles + 12-month sparkline", "1-click monthly ROI report (PDF + CSV)", "AI Knowledge Base management UI (search/filter/delete/JSON export-import)", "ICT/Windows as 8th discipline"] },
        { title: "Improved", items: ["Schema-aware AI with Confidence Guardrails (v1.4)", "Internal vs Customer split PDF export", "Whitelabel branding panel (logo + accent + tagline)"] },
        { title: "Under the hood", items: ["IndexedDB schema v7 (ai_roi_snapshots store)", "229 pure-Node tests, ESLint clean", "Asynchronous PDF/DOCX parsing via Electron IPC"] },
      ],
    },
    pricing: {
      label: "Packages",
      title: "Transparent rates — no tricks",
      subtitle:
        "No misleading discount badges or temporary marketing promotions. Just clear rates per month or per year.",
      popular: "Most popular",
      cta: "Order now",
      cloudAddon: {
        line: "Optional cloud-portal sync — give clients online access (+€24.99/month)",
        standardNote: "Customer portal: upgrade to Professional.",
      },
      billing: {
        monthly: "Monthly",
        annual: "Annual",
      },
      annualEquiv: "equiv.",
      microSub: "Includes all updates & support",
      period: {
        monthly: "/month",
        annual: "/year",
      },
      tiers: [
        {
          id: "standard",
          name: "Standard",
          desc: "For the small installer — 3 core disciplines.",
          prices: { monthly: "€24.95", annual: "€299.40" },
          features: [
            "3 disciplines: CCTV, Access control & Intrusion alarm (IMI/BORG)",
            "PDF export Internal / Customer (split flows)",
            "BORG battery measurement (multi-cell)",
            ".mhr AES-256 backup / restore",
            "Ed25519 licence signing",
          ],
          note: "Without photo embedding, logbook, AI Assistant & scheduler.",
        },
        {
          id: "professional",
          name: "Advanced",
          desc: "Standard + 2 disciplines, photos and logbook.",
          prices: { monthly: "€39.95", annual: "€479.40" },
          features: [
            "Everything in Standard",
            "+ 2 disciplines: Intercom & OP tasks (NEN 2654)",
            "Photo embedding + CCTV Image Book generator",
            "Logbook function (focused on OP tasks)",
          ],
          note: "AI Assistant & global scheduler are in Expert.",
        },
        {
          id: "expert",
          name: "Expert",
          desc: "All 8 disciplines, AI Assistant and full logbooks.",
          prices: { monthly: "€59.95", annual: "€719.40" },
          features: [
            "All 8 disciplines (incl. Fire, Evacuation/OAI & ICT/Windows)",
            "Offline AI Assistant (XLSX/CSV/DOCX classification)",
            "Logbooks per discipline (full)",
            "Global scheduler / NEN maintenance agenda",
            "Efficiency Cockpit: ROI dashboard + monthly report",
            "Whitelabel / OEM branding",
            "Priority support & direct updates",
          ],
        },
      ],
    },
    trial: {
      label: "Free Trial License",
      badge: "7 days · Expert · 100% functional",
      title: "Try SecureMaintPro free for 7 days in the field",
      subtitle:
        "Request a free, fully functional 7-day Expert trial license. Experience all 8 disciplines, including the automatic NEN inspection planner and photo annotations, right on your own hardware.",
      stepsTitle: "How does the request work?",
      downloadBtn: "Download SecureMaintPro (.EXE)",
      steps: [
        {
          title: "Download the software",
          desc: "Download the standalone Windows application via the button below. No installation required.",
        },
        {
          title: "Copy your Hardware ID",
          desc: "Launch the software on your laptop. The activation screen opens automatically and shows your unique, local Hardware ID (e.g. SMP-ABCD-1234).",
        },
        {
          title: "Request your trial license",
          desc: "Fill in the form and include your Hardware ID. We'll send your personal trial license (.lic) straight to your mailbox within a few hours.",
        },
      ],
      form: {
        title: "Trial license request form",
        company: "Company name",
        companyPh: "Your company name",
        name: "Contact name",
        namePh: "First and last name",
        email: "Email address",
        emailPh: "you@company.com",
        phone: "Phone number",
        phonePh: "+31 6 12 34 56 78",
        hardwareId: "Your unique Hardware ID",
        hardwareIdPh: "e.g. SMP-ABCD-1234",
        submit: "Request free trial license",
        noteBefore:
          "Your request is processed manually or via our system. For direct questions or support, email us at",
      },
      success: {
        title: "Request sent!",
        body: "Your mail client has opened with your trial license request. Send the email; you'll receive your .lic file within a few hours.",
        close: "Close",
      },
      mail: {
        subject:
          "Aanvraag Gratis Testlicentie / Free Trial Request - SecureMaintPro",
        labels: {
          company: "Bedrijf / Company",
          name: "Contactpersoon / Contact",
          email: "E-mail / Email",
          phone: "Telefoon / Phone",
          hardwareId: "Hardware-ID",
        },
      },
      errors: {
        company: "Company name is required",
        name: "Name is required",
        email: "Enter a valid email address",
        phone: "Phone number is required",
        hardwareId: "Hardware ID is required",
      },
    },
    downloads: {
      label: "Resources & Downloads",
      title: "Get started right away — no account needed",
      subtitle:
        "Public access to the product documentation and the secure Windows installer.",
      manual: {
        title: "Installation Manual (PDF)",
        desc: "Complete step-by-step guide for installation, configuration and first use.",
        meta: "PDF · Bilingual (NL/EN) · 290 KB",
        btn: "Download Manual",
      },
      installer: {
        title: "Windows Installer (.exe)",
        desc: "Secure, signed installer for Windows 10/11. Includes auto-update.",
        meta: "SecureMaintPro-Setup-1.0.0.exe · 79 MB",
        platform: "v1.0.0 · Windows 10/11 · 64-bit",
        checksumLabel: "SHA-256",
        checksum: "344c261de9b4f134dfc9307c0ac4ca6c6fb140b5d7393abea7dcaeca22e901f9",
        btn: "Download Installer",
      },
      note: "Official release files (v1.0.0). Download directly — no account needed.",
      copied: "SHA-256 copied",
      soon: "Coming soon",
      started: "Download initiated",
    },
    activation: {
      label: "License Activation",
      title: "Activated in three simple steps",
      subtitle:
        "From installer to working license in minutes — fully offline.",
      hwMail: {
        btn: "Email Hardware ID",
        subject: "Submit Hardware ID - SecureMaintPro",
      },
      steps: [
        {
          title: "Download the Installer",
          desc: "Grab the signed Windows installer from the Downloads hub and run the installation.",
        },
        {
          title: "Auto-Fetch Hardware ID",
          desc: "On first launch, SecureMaintPro automatically reads your unique Hardware ID for license binding.",
        },
        {
          title: "Drag-and-drop securemaint.lic",
          desc: "Drag your received securemaint.lic file into the window — instantly activated, no internet required.",
        },
      ],
    },
    faq: {
      label: "Frequently asked questions",
      title: "Everything you want to know",
      subtitle:
        "Clear answers about offline licensing, data storage and security.",
      items: [
        {
          q: "How does an offline license work?",
          a: "The app generates a unique hardware code on your laptop. We cryptographically sign this (Ed25519) into a .lic file that the app validates 100% locally and offline.",
        },
        {
          q: "Where is my data stored?",
          a: "By default completely local on your C-drive — SecureMaintPro runs 100% offline, no internet required. The only exception is the optional cloud-portal sync (Professional & Expert): if you enable it, your engineers upload only the maintenance reports and work orders they select to your own isolated customer portal. Without that option, no data ever leaves your machine.",
        },
        {
          q: "Are software updates included?",
          a: "Yes. Both monthly and annual licenses grant unlimited access to all software updates, bug fixes and support during the active term.",
        },
        {
          q: "Can I modify or cancel an active license mid-term?",
          a: "No, SecureMaintPro enforces a strict non-refundable policy. Licenses are purchased upfront for the selected duration (monthly or annual) per unique Hardware ID. Mid-term cancellations, refunds, or pro-rata adjustments are explicitly excluded. Upgrading mid-term will initiate a fresh, full license cycle for the newly selected tier.",
        },
        {
          q: "What is the customer portal and do I need it?",
          a: "The customer portal is an online environment where your clients have 24/7 access to their maintenance reports and work orders. It is an optional add-on to Professional and Expert software. Without the portal all data stays local — with the portal your clients can log in themselves and sign reports.",
        },
        {
          q: "What does the customer portal cost?",
          a: "€24.99 per month (billed annually, ≈ €299.88/year). An unlimited number of users per client organisation is included.",
        },
        {
          q: "Is using the customer portal mandatory?",
          a: "No. The cloud sync in Professional and Expert software is entirely optional. Your engineers can choose whether or not to upload — if the license is not active the software simply keeps working offline.",
        },
        {
          q: "My client has Standard — can they still use the portal?",
          a: "Not directly. The portal is only available with Professional and Expert software licenses. An upgrade from Standard to Professional can be arranged for this.",
        },
        {
          q: "How secure is the data in the portal?",
          a: "Each client organisation is an isolated tenant — no other client can see the data. All login attempts, downloads and changes are logged in an audit trail. PDFs are stored encrypted.",
        },
      ],
    },
    contact: {
      label: "Order & Contact",
      title: "Order your license or ask a question",
      subtitle:
        "Fill in the form. On submit, your mail client opens automatically with a clean, pre-populated order.",
      fields: {
        name: "Client name",
        namePh: "First and last name",
        email: "Contact email",
        emailPh: "you@company.com",
        hardwareId: "Hardware ID",
        hardwareIdPh: "16 chars, e.g. 7F3A9B2C4D5E6F70",
        package: "Package",
        packagePh: "Select a package",
        duration: "Duration",
        durationPh: "Select duration",
        message: "Message (optional)",
        messagePh: "Any notes or configuration question...",
      },
      packages: {
        standard: "Standard",
        professional: "Professional",
        expert: "Expert",
        inquiry: "General inquiry",
      },
      durations: {
        monthly: "Monthly",
        annual: "Annual",
      },
      submit: "Send & Open Mail",
      success: {
        title: "Order ready!",
        body: "Your mail client has opened with a pre-populated order. Send the email to finalize your request.",
        close: "Close",
      },
      mail: {
        subject: "Nieuwe Bestelling / New Order - SecureMaintPro",
        labels: {
          client: "Klant / Client",
          tier: "Pakket / Tier",
          duration: "Looptijd / Duration",
          hardwareId: "Hardware-ID",
          message: "Bericht / Message",
        },
      },
      errors: {
        name: "Name is required",
        email: "Enter a valid email address",
        package: "Select a package",
        duration: "Select a duration",
        hardwareId: "Hardware ID must be exactly 16 alphanumeric characters",
      },
    },
    footer: {
      tagline:
        "The offline-first logbook and maintenance platform for security engineers.",
      product: "Product",
      resources: "Resources",
      legal: "Legal",
      links: {
        features: "Features",
        pricing: "Pricing",
        downloads: "Downloads",
        activation: "Activation",
        manual: "Manual",
        contact: "Contact",
        privacy: "Privacy Policy",
        terms: "Terms",
        compliance: "Compliance",
      },
      rights: "All rights reserved.",
      version: "",
    },
    compliance: {
      label: "Legal, Privacy & Compliance",
      title: "Aligned with NEN 2654 & built on Privacy by Design",
      cards: [
        {
          title: "Guidelines NEN 2654-1 & NEN 2654-2",
          desc: "The reporting and checklist modules for OP tasks are functionally aligned with the framework of NEN 2654-1 and NEN 2654-2. The software is not assessed or certified by VEB, CCV or NEN; we do strive to work according to their guidelines. It serves as a digital tool to efficiently record the mandatory registrations in a local logbook. The user remains fully responsible for the correct execution and verification of the standards in practice.",
        },
        {
          title: "Privacy & GDPR",
          desc: "SecureMaintPro is built on Privacy by Design. All operational data entered is stored strictly locally on the licensee's hardware. We have no remote access to your databases or customer data. The licensee acts legally as the Data Controller in accordance with the GDPR.",
        },
        {
          title: "AI runs 100% client-side",
          desc: "XLSX, CSV and DOCX are parsed locally via Electron-IPC. No files, no tokens, no telemetry ever leaves the device.",
        },
      ],
      contactLabel: "Inquiries, license activations or support?",
      contactBtn: "Email us",
    },
    terms: {
      title: "General Terms of Sale",
      subtitle: "Please read our license and sales terms carefully.",
      close: "Close",
      link: "General Terms of Sale",
      articles: [
        {
          title: "Article 1 — License Structure & Hardware Binding",
          items: [
            "1.1 Licenses for SecureMaintPro are issued exclusively on the basis of a unique Hardware ID (device fingerprint).",
            "1.2 A generated license file (.lic) is strictly bound to the specific laptop/machine for which it was requested. Transferring an active license to another machine or hardware ID is not permitted mid-term.",
            "1.3 The Hardware ID must be copied directly from the software and can therefore hardly be submitted incorrectly. The Licensee is fully responsible for providing the Hardware ID truthfully and correctly. Because the software operates entirely offline, the Licensor cannot verify its accuracy. If an incorrect Hardware ID is provided, the license will still be generated for that (incorrect) ID; no refund or free re-issue will be granted and a new license must be purchased.",
            "1.4 In the event of loss, theft, defect or replacement of the laptop/machine to which the license is bound, the license cannot be transferred or restored. Because activation takes place entirely offline, the Licensor cannot verify this. In such cases 'all sales are final' applies and a new license must be purchased.",
          ],
        },
        {
          title: 'Article 2 — "All Sales Final" and Upgrades',
          items: [
            "2.1 SecureMaintPro enforces a strict non-refundable policy. Given the nature of digital, offline activation, refunds, cancellation or pro-rata settlement of amounts already paid are excluded.",
            "2.2 If a Licensee wishes to upgrade mid-term (for example from the Standard to the Expert package), the current license period lapses. A new, full license term (monthly or annual) starts at that moment at the rate of the newly selected package. Remaining months of the old license are not financially compensated or offset.",
          ],
        },
        {
          title: "Article 3 — Software Updates & Support",
          items: [
            "3.1 Monthly and annual subscription licenses grant the right to all functional software updates, bug fixes and support during the active, paid contract period.",
            "3.2 SecureMaintPro is offered exclusively as a monthly or annual subscription license. Future major architectural upgrades (such as a transition to v2.0.0) are made available within an active subscription in accordance with the update policy in force at that time.",
          ],
        },
        {
          title: "Article 4 — Anti-Fraud and Account Lockout",
          items: [
            "4.1 The software features built-in mechanisms for offline fraud detection, including anti-clock-fraud (system time manipulation). If the system detects manipulation of the internal clock or the license structure, the application is immediately locked locally.",
            "4.2 The Licensee is solely responsible for correctly setting and maintaining the current system/network time on the machine. A local lockout resulting from detected clock fraud (system time manipulation) will not be reviewed, reversed or restored by the Licensor. In that case the right of use on the machine concerned lapses and a new license must be purchased.",
          ],
        },
      ],
    },
    shop: {
      nav: "Order",
      vat: "excl. VAT",
      title: "Order your license",
      subtitle: "Pick your plan and pay instantly via iDEAL. Your invoice and activation code arrive by email.",
      billingNote: "All prices are excl. 21% VAT.",
      addonTitle: "Add the customer portal",
      addonDesc: "Unlimited users · online report viewing & digital signing.",
      addonStandard: "Upgrade to Professional or Expert to add the customer portal.",
      choose: "Choose",
      soon: "Coming soon",
      checkoutTitle: "Complete your order",
      summary: "Your order",
      emailLabel: "Email address",
      emailPh: "you@company.com",
      companyLabel: "Company name",
      companyPh: "Your company name",
      kvkLabel: "Chamber of Commerce no. (optional)",
      kvkPh: "12345678",
      termsPrefix: "I agree to the",
      termsLink: "Terms & Conditions",
      pay: "Pay via iDEAL",
      processing: "Processing…",
      back: "Back to shop",
      subtotal: "Subtotal (excl. VAT)",
      vatLine: "VAT (21%)",
      total: "Total (incl. VAT)",
      portalAddon: "Customer portal add-on",
      portalYearly: "Annual license",
      addonPriceLabel: "+€299.88/year",
      perYear: "/year",
      oneTime: "one-time",
      termsRequired: "Please accept the terms to continue.",
      emailRequired: "Please enter a valid email address.",
      unavailable: "Online payments are being activated soon. For now, please contact us by email.",
      error: "Something went wrong starting the payment. Please try again later.",
      orderEmail: "Send order by email",
      mailSubject: "SecureMaintPro order",
      mailIntro: "I would like to order the following SecureMaintPro license:",
      mailSent: "Your order is ready in your email app — send the email to complete your order.",
      thankTitle: "Thank you for your order!",
      thankBody: "You will receive an email with your invoice and activation code within a few minutes. Use the activation code in the SecureMaintPro app to bind your license.",
      thankActivate: "Activation instructions",
      thankHome: "Back to home",
      activateTitle: "Activate your license",
      activateSubtitle: "Follow these steps after you receive your activation code by email.",
    },
    portal: {
      navLabel: "Customer portal",
      eyebrow: "Customer portal add-on",
      title: "Give your clients 24/7 access to their reports",
      lead: "With the optional cloud sync in our Professional and Expert software, your engineers automatically upload maintenance reports and work orders to the customer portal. Your client logs in at portaal.securemaintpro.nl and has instant access.",
      features: [
        { title: "Sign reports", desc: "Client signs maintenance reports digitally — right in the browser." },
        { title: "View work orders", desc: "Full overview of all work orders and reported defects." },
        { title: "Multi-user · unlimited", desc: "Unlimited users per organisation with roles (admin · approver · viewer)." },
        { title: "Multi-tenant isolated", desc: "Strict data separation per organisation + full audit log." },
      ],
      card: {
        eyebrow: "Annual license",
        title: "SecureMaintPro · Customer portal",
        price: "€24.99",
        period: "/month",
        year: "≈ €299.88 per year · fixed price · billed annually",
        includes: [
          "1 year access to the customer portal",
          "Unlimited number of users",
          "All features: signing, work-order view, audit log",
          "Automatic sync from Pro/Expert desktop software",
          "Multi-tenant isolated · end-to-end logged",
        ],
        eligibility: "Available for clients with Professional or Expert desktop software. Standard clients can upgrade to use the portal.",
        ctaRequest: "Request customer portal",
        ctaLogin: "Already a client? Log in",
      },
    },
    academy: {
      label: "Academy",
      title: "SecureMaintPro Academy",
      subtitle: "Watch step-by-step video tutorials and see the software in action — from installation to reporting.",
      videoTitle: "Software instruction — step-by-step explanation",
      videoDesc: "Checklists, maintenance, reporting and settings: everything explained clearly in short videos on our YouTube channel.",
      cta: "Watch videos on YouTube",
      channelNote: "New videos are published regularly on our channel.",
      chips: ["Checklists", "Maintenance", "Reporting", "Settings"],
      linkedinCta: "Follow us on LinkedIn",
      linkedinNote: "Stay up to date with updates, new releases and tips.",
    },
  },
};
